import { useCallback, useEffect, useMemo, useState } from 'react';
import { useActivePlayback } from '../context/ActivePlaybackContext';
import {
  bridgeLaunchUrl,
  createLaunchCode,
  decideLaunchState,
  type LaunchPhase,
  type LaunchState,
} from '../utils/bridgeLaunch';

const IFRAME_LIFETIME_MS = 1_500;
const WAIT_TICK_MS = 1_000;
const CONNECTED_CLEAR_MS = 3_000;

function openLaunchLink(code: string, launchEventId?: string): void {
  const iframe = document.createElement('iframe');
  iframe.style.display = 'none';
  iframe.src = bridgeLaunchUrl(code, launchEventId);
  document.body.appendChild(iframe);
  setTimeout(() => iframe.remove(), IFRAME_LIFETIME_MS);
}

export function useBridgeLauncher(eventId: string | undefined, launchEventId?: string): {
  state: LaunchState;
  launch: () => void;
  recheck: () => void;
  dismiss: () => void;
} {
  const { activePlayback, bridgeLaunchAck, bridgeStatusFor } = useActivePlayback();
  const [launchCodes, setLaunchCodes] = useState<string[]>([]);
  const [phase, setPhase] = useState<LaunchPhase>('launch');
  const [clickedAtMs, setClickedAtMs] = useState<number | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());

  const bridgeKind = bridgeStatusFor(eventId).kind;
  const bridgeLive = bridgeKind === 'live-here' || bridgeKind === 'live-elsewhere';

  const state = useMemo(() => decideLaunchState({
    codes: launchCodes,
    phase,
    clickedAtMs,
    nowMs,
    ack: bridgeLaunchAck,
    bridgeLive,
    liveVersion: bridgeLive ? activePlayback?.bridgeVersion ?? null : null,
  }), [launchCodes, phase, clickedAtMs, nowMs, bridgeLaunchAck, bridgeLive, activePlayback?.bridgeVersion]);

  const timed = state.kind === 'waiting' || state.kind === 'rechecking';

  useEffect(() => {
    if (!timed) return;
    const timer = setInterval(() => setNowMs(Date.now()), WAIT_TICK_MS);
    return () => clearInterval(timer);
  }, [timed]);

  const dismiss = useCallback(() => {
    setLaunchCodes([]);
    setClickedAtMs(null);
    setPhase('launch');
  }, []);

  useEffect(() => {
    if (state.kind !== 'connected') return;
    const timer = setTimeout(dismiss, CONNECTED_CLEAR_MS);
    return () => clearTimeout(timer);
  }, [state.kind, dismiss]);

  const launch = useCallback(() => {
    const code = createLaunchCode();
    const now = Date.now();
    setLaunchCodes([code]);
    setPhase('launch');
    setClickedAtMs(now);
    setNowMs(now);
    openLaunchLink(code, launchEventId);
  }, [launchEventId]);

  const recheck = useCallback(() => {
    const code = createLaunchCode();
    const now = Date.now();
    setLaunchCodes((codes) => [...codes, code]);
    setPhase('recheck');
    setClickedAtMs(now);
    setNowMs(now);
    openLaunchLink(code, launchEventId);
  }, [launchEventId]);

  return { state, launch, recheck, dismiss };
}
